import {
  DeleteOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  App,
  Button,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
  type TableProps,
} from 'antd';
import dayjs from 'dayjs';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  type MouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_ROUTE_SORT } from '@/hooks/useRoutesLibrary';
import {
  ROUTE_VARIANT_KEYS,
  pickCanonicalVariant,
  type RouteListSort,
  type RouteResponse,
  type RouteSortField,
  type RouteVariantKey,
  type VariantSummary,
} from '@/types/routes';

const ROUTE_ID_PREFIX_LEN = 12;
const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

type AntSortOrder = 'ascend' | 'descend' | null;

const SORTABLE_COLUMN_KEYS: readonly RouteSortField[] = [
  'length_km',
  'created_at',
  'updated_at',
];

function isSortField(key: unknown): key is RouteSortField {
  return (
    typeof key === 'string' &&
    (SORTABLE_COLUMN_KEYS as readonly string[]).includes(key)
  );
}

function sortOrderFor(field: RouteSortField, sort: RouteListSort): AntSortOrder {
  if (sort.field !== field) return null;
  return sort.direction === 'asc' ? 'ascend' : 'descend';
}

const VARIANT_COLORS: Record<RouteVariantKey, string> = {
  gis: 'gold',
  iteko: 'purple',
  barrier_aware: 'green',
};

export interface VariantToggleHandlers {
  readonly getToggles: (routeId: string) => Readonly<Record<RouteVariantKey, boolean>>;
  readonly setToggle: (
    routeId: string, variant: RouteVariantKey, visible: boolean,
  ) => void;
}

export interface RoutesLibraryTableHandle {
  /** Scrolls the row for `routeId` into view, if present on the page. */
  readonly scrollToRoute: (routeId: string) => void;
}

interface RoutesLibraryTableProps {
  readonly items: readonly RouteResponse[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly loading: boolean;
  readonly selectedRowKeys: readonly string[];
  readonly focusedRouteId: string | null;
  readonly bulkBusy: boolean;
  readonly selectAllBusy: boolean;
  readonly toggles: VariantToggleHandlers;
  readonly sort: RouteListSort;
  readonly onSortChange: (next: RouteListSort) => void;
  readonly onPageChange: (page: number) => void;
  readonly onSelectionChange: (keys: readonly string[]) => void;
  readonly onSelectAllAcrossPages: () => void;
  readonly onClearSelection: () => void;
  readonly onRowClick: (route: RouteResponse) => void;
  readonly onBulkDelete: () => void;
  readonly onBulkRecompute: () => void;
}

const RoutesLibraryTable = forwardRef<
  RoutesLibraryTableHandle,
  RoutesLibraryTableProps
>(function RoutesLibraryTable(props, ref) {
  const {
    items,
    total,
    page,
    pageSize,
    loading,
    selectedRowKeys,
    focusedRouteId,
    bulkBusy,
    selectAllBusy,
    toggles,
    sort,
    onSortChange,
    onPageChange,
    onSelectionChange,
    onSelectAllAcrossPages,
    onClearSelection,
    onRowClick,
    onBulkDelete,
    onBulkRecompute,
  } = props;
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      scrollToRoute: (routeId: string) => {
        const node = wrapperRef.current?.querySelector<HTMLElement>(
          `[data-row-key="${cssEscape(routeId)}"]`,
        );
        node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    }),
    [],
  );

  const columns: TableColumnsType<RouteResponse> = useMemo(
    () => [
      {
        title: t('routes.library.columns.id'),
        dataIndex: 'route_id',
        key: 'route_id',
        width: 110,
        render: (value: string) => (
          <Tooltip title={value}>
            <Typography.Text code style={{ fontSize: 11 }}>
              {value.length > ROUTE_ID_PREFIX_LEN
                ? `${value.slice(0, ROUTE_ID_PREFIX_LEN)}…`
                : value}
            </Typography.Text>
          </Tooltip>
        ),
      },
      {
        title: t('routes.library.columns.from'),
        dataIndex: 'from_address',
        key: 'from_address',
        ellipsis: true,
        render: (value: string | null) =>
          value ?? <Typography.Text type="secondary">—</Typography.Text>,
      },
      {
        title: t('routes.library.columns.to'),
        dataIndex: 'to_address',
        key: 'to_address',
        ellipsis: true,
        render: (value: string | null) =>
          value ?? <Typography.Text type="secondary">—</Typography.Text>,
      },
      {
        title: t('routes.library.columns.length'),
        key: 'length_km',
        width: 100,
        align: 'right',
        sorter: true,
        sortOrder: sortOrderFor('length_km', sort),
        render: (_value, row) => <LengthCell route={row} />,
      },
      {
        title: t('routes.library.columns.variants'),
        key: 'variants',
        width: 180,
        render: (_value, row) => (
          <VariantsToggleCell
            route={row}
            toggles={toggles}
          />
        ),
      },
      {
        title: t('routes.library.columns.flags'),
        key: 'flags',
        width: 70,
        render: (_value, row) => <FlagsCell route={row} />,
      },
      {
        title: t('routes.library.columns.created'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 165,
        sorter: true,
        sortOrder: sortOrderFor('created_at', sort),
        render: (value: string) => dayjs(value).format(DATE_TIME_FORMAT),
      },
      {
        title: t('routes.library.columns.updated'),
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 165,
        sorter: true,
        sortOrder: sortOrderFor('updated_at', sort),
        render: (value: string) => dayjs(value).format(DATE_TIME_FORMAT),
      },
    ],
    [t, toggles, sort],
  );

  const handleTableChange: TableProps<RouteResponse>['onChange'] = (
    _pagination,
    _filters,
    sorter,
    extra,
  ) => {
    if (extra.action !== 'sort') return;
    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!single?.order || !isSortField(single.columnKey)) {
      // Third click clears the column sorter — fall back to the default.
      onSortChange(DEFAULT_ROUTE_SORT);
      return;
    }
    onSortChange({
      field: single.columnKey,
      direction: single.order === 'ascend' ? 'asc' : 'desc',
    });
  };

  const rowSelection: TableProps<RouteResponse>['rowSelection'] = {
    selectedRowKeys: [...selectedRowKeys],
    onChange: (keys) => onSelectionChange(keys.map(String)),
    preserveSelectedRowKeys: true,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_NONE,
      {
        key: 'all-pages',
        text: t('routes.library.bulk.selectAllPages', { count: total }),
        onSelect: onSelectAllAcrossPages,
      },
    ],
    getCheckboxProps: () => ({
      onClick: (event: MouseEvent) => event.stopPropagation(),
    }),
  };

  const handleBulkDeleteClick = () => {
    modal.confirm({
      title: t('routes.library.bulk.deleteConfirmTitle', { count: selectedRowKeys.length }),
      content: t('routes.library.bulk.deleteConfirmDescription'),
      okText: t('common.delete'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: onBulkDelete,
    });
  };

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <BulkActionBar
        selectedCount={selectedRowKeys.length}
        total={total}
        busy={bulkBusy}
        selectAllBusy={selectAllBusy}
        onDelete={handleBulkDeleteClick}
        onRecompute={onBulkRecompute}
        onSelectAllAcrossPages={onSelectAllAcrossPages}
        onClearSelection={onClearSelection}
      />
      <div ref={wrapperRef}>
        <Table<RouteResponse>
          size="small"
          rowKey="route_id"
          columns={columns}
          dataSource={items as RouteResponse[]}
          loading={loading}
          rowSelection={rowSelection}
          onChange={handleTableChange}
          showSorterTooltip={false}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            size: 'small',
            onChange: onPageChange,
            showTotal: (totalCount, range) =>
              t('routes.library.pagination.showTotal', {
                from: range[0],
                to: range[1],
                total: totalCount,
              }),
          }}
          onRow={(record) => ({
            onClick: () => onRowClick(record),
            style: {
              cursor: 'pointer',
              background:
                record.route_id === focusedRouteId ? '#f6ffed' : undefined,
            },
          })}
          scroll={{ x: 'max-content' }}
        />
      </div>
    </Space>
  );
});

export default RoutesLibraryTable;

interface BulkActionBarProps {
  readonly selectedCount: number;
  readonly total: number;
  readonly busy: boolean;
  readonly selectAllBusy: boolean;
  readonly onDelete: () => void;
  readonly onRecompute: () => void;
  readonly onSelectAllAcrossPages: () => void;
  readonly onClearSelection: () => void;
}

function BulkActionBar({
  selectedCount,
  total,
  busy,
  selectAllBusy,
  onDelete,
  onRecompute,
  onSelectAllAcrossPages,
  onClearSelection,
}: BulkActionBarProps) {
  const { t } = useTranslation();
  const disabled = selectedCount === 0 || busy;
  const canSelectAll = selectedCount < total && !busy;
  return (
    <Space size="small" align="center" wrap>
      <Typography.Text type="secondary">
        {t('routes.library.bulk.selected', { count: selectedCount })}
      </Typography.Text>
      {canSelectAll && (
        <Button
          size="small"
          type="link"
          style={{ padding: 0 }}
          loading={selectAllBusy}
          onClick={onSelectAllAcrossPages}
        >
          {t('routes.library.bulk.selectAllPages', { count: total })}
        </Button>
      )}
      {selectedCount > 0 && !busy && (
        <Button size="small" type="link" style={{ padding: 0 }} onClick={onClearSelection}>
          {t('routes.library.bulk.clearSelection')}
        </Button>
      )}
      <Button
        size="small"
        icon={<ReloadOutlined />}
        onClick={onRecompute}
        disabled={disabled}
        loading={busy}
      >
        {t('routes.library.bulk.recompute')}
      </Button>
      <Button
        size="small"
        danger
        icon={<DeleteOutlined />}
        onClick={onDelete}
        disabled={disabled}
      >
        {t('routes.library.bulk.delete')}
      </Button>
    </Space>
  );
}

interface LengthCellProps {
  readonly route: RouteResponse;
}

function LengthCell({ route }: LengthCellProps) {
  const { t } = useTranslation();
  const canonical = pickCanonicalVariant(route.variants);
  if (!canonical) {
    return <Typography.Text type="secondary">—</Typography.Text>;
  }
  return (
    <Typography.Text>
      {canonical.length_km.toFixed(1)} {t('common.units.kilometers')}
    </Typography.Text>
  );
}

interface FlagsCellProps {
  readonly route: RouteResponse;
}

function FlagsCell({ route }: FlagsCellProps) {
  const { t } = useTranslation();
  const barrierAware = route.variants.find((v) => v.variant === 'barrier_aware');
  const appliedCount = barrierAware?.applied_barriers.length ?? 0;
  const residualCount = barrierAware?.residual_barriers.length ?? 0;
  return (
    <Space size={4}>
      {appliedCount > 0 && (
        <Tooltip title={t('routes.library.flags.applied', { count: appliedCount })}>
          <Tag color="blue">{appliedCount}</Tag>
        </Tooltip>
      )}
      {residualCount > 0 && (
        <Tooltip title={t('routes.library.flags.residual', { count: residualCount })}>
          <Tag color="orange" icon={<WarningOutlined />}>
            {residualCount}
          </Tag>
        </Tooltip>
      )}
    </Space>
  );
}

interface VariantsToggleCellProps {
  readonly route: RouteResponse;
  readonly toggles: VariantToggleHandlers;
}

function VariantsToggleCell({ route, toggles }: VariantsToggleCellProps) {
  const { t } = useTranslation();
  const current = toggles.getToggles(route.route_id);
  const available = new Set(route.variants.map((v) => v.variant));
  return (
    <Space size={4} onClick={(e) => e.stopPropagation()}>
      {ROUTE_VARIANT_KEYS.map((variant) => {
        const has = available.has(variant);
        const on = has && current[variant];
        const color = on ? VARIANT_COLORS[variant] : 'default';
        const label = t(`routes.variantShort.${variant}`);
        return (
          <Tooltip
            key={variant}
            title={
              has
                ? t(`routes.variantTooltip.${variant}`)
                : t('routes.variantTooltip.unavailable')
            }
          >
            <Tag
              color={color}
              style={{
                cursor: has ? 'pointer' : 'not-allowed',
                opacity: has ? 1 : 0.4,
                userSelect: 'none',
                margin: 0,
              }}
              onClick={() => {
                if (!has) return;
                toggles.setToggle(route.route_id, variant, !current[variant]);
              }}
            >
              {label}
            </Tag>
          </Tooltip>
        );
      })}
    </Space>
  );
}

// silence unused import if the linter picks it up; VariantSummary is used
// indirectly through type narrowing in FlagsCell/LengthCell.
export type { VariantSummary };

function cssEscape(value: string): string {
  // Minimal CSS.escape polyfill for querySelector safety on hash-like
  // route_ids (they contain colons). Everything outside [A-Za-z0-9_-]
  // is replaced with a backslash escape sequence.
  return value.replace(/[^A-Za-z0-9_-]/g, (ch) => `\\${ch}`);
}
